/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Search, Edit, Archive, RotateCcw, Flag, FlagOff, Send, History,
  RefreshCw, BarChart3, ListChecks, AlertTriangle, Loader2, Eye,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { API, getAuthHeader, STATUTS, FLAG_CATEGORIES, formatDate, formatEUR, StatusBadge } from './communityplaceShared';
import { EditDemandeModal, ArchiveDemandeModal, FlagDemandeModal, ForceRemiseModal, HistoryModal } from './CommunityplaceModals';

export function ListeTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filtres
  const [search, setSearch] = useState('');
  const [statut, setStatut] = useState('all');
  const [territoire, setTerritoire] = useState('');
  const [days, setDays] = useState('all');
  const [showArchived, setShowArchived] = useState(false);

  // Pagination
  const [skip, setSkip] = useState(0);
  const limit = 25;

  // Modals
  const [editModal, setEditModal] = useState({ open: false, demande: null });
  const [archiveModal, setArchiveModal] = useState({ open: false, demande: null });
  const [flagModal, setFlagModal] = useState({ open: false, demande: null });
  const [remiseModal, setRemiseModal] = useState({ open: false, demande: null });
  const [historyModal, setHistoryModal] = useState({ open: false, demande: null });

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip, limit };
      if (search) params.search = search;
      if (statut !== 'all') params.statut = statut;
      if (territoire) params.territoire = territoire;
      if (days !== 'all') params.days = parseInt(days, 10);
      if (!showArchived) params.is_archived = false;
      const res = await axios.get(`${API}/list`, { params, headers: getAuthHeader() });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur chargement Communityplace');
    } finally {
      setLoading(false);
    }
  }, [skip, search, statut, territoire, days, showArchived]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const refresh = () => fetchList();

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label htmlFor="cp-search" className="text-xs">Recherche</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
              <Input
                id="cp-search"
                data-testid="sa-cp-search-input"
                placeholder="Email, nom, ville, O'SCOP…"
                className="pl-8"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setSkip(0); }}
              />
            </div>
          </div>
          <div className="w-[160px]">
            <Label className="text-xs">Statut</Label>
            <Select value={statut} onValueChange={(v) => { setStatut(v); setSkip(0); }}>
              <SelectTrigger data-testid="sa-cp-statut-select"><SelectValue placeholder="Tous" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {STATUTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[160px]">
            <Label className="text-xs">Territoire</Label>
            <Input
              data-testid="sa-cp-territoire-input"
              placeholder="ex: Guadeloupe"
              value={territoire}
              onChange={(e) => { setTerritoire(e.target.value); setSkip(0); }}
            />
          </div>
          <div className="w-[140px]">
            <Label className="text-xs">Période</Label>
            <Select value={days} onValueChange={(v) => { setDays(v); setSkip(0); }}>
              <SelectTrigger data-testid="sa-cp-days-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="7">7 jours</SelectItem>
                <SelectItem value="30">30 jours</SelectItem>
                <SelectItem value="90">90 jours</SelectItem>
                <SelectItem value="365">1 an</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 pb-1">
            <input
              type="checkbox"
              id="show-archived"
              data-testid="sa-cp-show-archived"
              checked={showArchived}
              onChange={(e) => { setShowArchived(e.target.checked); setSkip(0); }}
            />
            <Label htmlFor="show-archived" className="text-sm">Voir archivées</Label>
          </div>
          <Button onClick={refresh} variant="outline" size="sm" data-testid="sa-cp-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Actualiser
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        <div className="text-sm text-gray-600 mb-3" data-testid="sa-cp-total-count">
          <strong>{total}</strong> demande{total > 1 ? 's' : ''} — page {Math.floor(skip / limit) + 1}/{Math.max(1, Math.ceil(total / limit))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Chargement…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-gray-500">Aucune demande pour ces filtres.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sa-cp-table">
              <thead className="bg-gray-50 text-gray-700 text-xs uppercase">
                <tr>
                  <th className="text-left p-2">Réf. / Date</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-left p-2">Demandeur</th>
                  <th className="text-left p-2">Territoire</th>
                  <th className="text-right p-2">Prix TTC</th>
                  <th className="text-left p-2">Statut</th>
                  <th className="text-right p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((d) => (
                  <tr key={d.id} className="border-b hover:bg-gray-50" data-testid={`sa-cp-row-${d.id}`}>
                    <td className="p-2">
                      <div className="font-mono text-xs">{d.oscop_id || d.id?.slice(0, 8)}</div>
                      <div className="text-xs text-gray-500">{formatDate(d.created_at)}</div>
                    </td>
                    <td className="p-2">{d.type_demande || '—'}</td>
                    <td className="p-2">
                      <div className="font-medium">{d.prenom} {d.nom}</div>
                      <div className="text-xs text-gray-500">{d.email}</div>
                    </td>
                    <td className="p-2">{d.territoire || '—'}</td>
                    <td className="p-2 text-right font-medium">{formatEUR(d.prix_ttc)}</td>
                    <td className="p-2"><StatusBadge demande={d} /></td>
                    <td className="p-2">
                      <div className="flex gap-1 justify-end">
                        <Button size="icon" variant="ghost" title="Détail & Historique"
                          data-testid={`sa-cp-history-btn-${d.id}`}
                          onClick={() => setHistoryModal({ open: true, demande: d })}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" title="Éditer"
                          data-testid={`sa-cp-edit-btn-${d.id}`}
                          onClick={() => setEditModal({ open: true, demande: d })}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {d.is_assigned && (
                          <Button size="icon" variant="ghost" title="Forcer la remise"
                            data-testid={`sa-cp-force-remise-btn-${d.id}`}
                            onClick={() => setRemiseModal({ open: true, demande: d })}>
                            <Send className="h-4 w-4 text-blue-600" />
                          </Button>
                        )}
                        <Button size="icon" variant="ghost"
                          title={d.is_flagged ? 'Lever signalement' : 'Signaler'}
                          data-testid={`sa-cp-flag-btn-${d.id}`}
                          onClick={() => setFlagModal({ open: true, demande: d })}>
                          {d.is_flagged ? <FlagOff className="h-4 w-4 text-red-600" /> : <Flag className="h-4 w-4" />}
                        </Button>
                        <Button size="icon" variant="ghost"
                          title={d.is_archived ? 'Restaurer' : 'Archiver'}
                          data-testid={`sa-cp-archive-btn-${d.id}`}
                          onClick={() => setArchiveModal({ open: true, demande: d })}>
                          {d.is_archived ? <RotateCcw className="h-4 w-4 text-emerald-600" /> : <Archive className="h-4 w-4" />}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex items-center justify-between mt-4">
              <Button size="sm" variant="outline" disabled={skip <= 0}
                onClick={() => setSkip(Math.max(0, skip - limit))}
                data-testid="sa-cp-prev-btn">← Précédent</Button>
              <Button size="sm" variant="outline" disabled={skip + limit >= total}
                onClick={() => setSkip(skip + limit)}
                data-testid="sa-cp-next-btn">Suivant →</Button>
            </div>
          </div>
        )}

        <EditDemandeModal modal={editModal} setModal={setEditModal} onDone={refresh} />
        <ArchiveDemandeModal modal={archiveModal} setModal={setArchiveModal} onDone={refresh} />
        <FlagDemandeModal modal={flagModal} setModal={setFlagModal} onDone={refresh} />
        <ForceRemiseModal modal={remiseModal} setModal={setRemiseModal} onDone={refresh} />
        <HistoryModal modal={historyModal} setModal={setHistoryModal} />
      </CardContent>
    </Card>
  );
}

// =============================================================================
// MODALS
// =============================================================================

export function StatsTab() {
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState('30');
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/stats`, { params: { days }, headers: getAuthHeader() });
      setStats(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur stats');
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  if (loading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  if (!stats) return null;

  return (
    <div className="space-y-6" data-testid="sa-cp-stats-content">
      <div className="flex items-center gap-3">
        <Label>Période :</Label>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-[150px]" data-testid="sa-cp-stats-days"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7 jours</SelectItem>
            <SelectItem value="30">30 jours</SelectItem>
            <SelectItem value="90">90 jours</SelectItem>
            <SelectItem value="365">1 an</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total demandes" value={stats.total} color="blue" testid="sa-cp-stat-total" />
        <StatCard label="Publiées" value={stats.published} color="indigo" testid="sa-cp-stat-published" />
        <StatCard label="Assignées" value={stats.assigned} color="emerald" testid="sa-cp-stat-assigned" />
        <StatCard label="GMV TTC" value={formatEUR(stats.gmv_ttc)} color="amber" testid="sa-cp-stat-gmv" />
        <StatCard label="Conversion" value={`${stats.conversion_rate_pct}%`} color="purple" testid="sa-cp-stat-conversion" />
        <StatCard label="Archivées" value={stats.archived} color="gray" testid="sa-cp-stat-archived" />
        <StatCard label="Signalées" value={stats.flagged} color="red" testid="sa-cp-stat-flagged" />
        <StatCard label="Suspendues" value={stats.suspended} color="amber" testid="sa-cp-stat-suspended" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-sm">Top Territoires</CardTitle></CardHeader>
          <CardContent>
            {(stats.top_territoires || []).map((t, i) => (
              <div key={i} className="flex justify-between py-1 border-b last:border-0">
                <span>{t.territoire}</span><Badge variant="secondary">{t.count}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-sm">Top Types de demande</CardTitle></CardHeader>
          <CardContent>
            {(stats.top_types || []).map((t, i) => (
              <div key={i} className="flex justify-between py-1 border-b last:border-0">
                <span>{t.type_demande}</span><Badge variant="secondary">{t.count}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, testid }) {
  const palette = {
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    purple: 'bg-purple-50 border-purple-200 text-purple-900',
    gray: 'bg-gray-50 border-gray-200 text-gray-900',
    red: 'bg-red-50 border-red-200 text-red-900',
  }[color] || 'bg-gray-50 border-gray-200';
  return (
    <div className={`border rounded-lg p-4 ${palette}`} data-testid={testid}>
      <div className="text-xs uppercase opacity-75">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

// =============================================================================
// ONGLET SIGNALEMENTS
// =============================================================================
export function SignalementsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flagModal, setFlagModal] = useState({ open: false, demande: null });

  const fetchSignalements = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/signalements`, { headers: getAuthHeader() });
      setItems(res.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSignalements(); }, [fetchSignalements]);

  if (loading) return <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          🚨 Demandes signalées ({items.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-gray-500 text-center py-8">Aucune demande signalée — tout va bien&nbsp;!</div>
        ) : (
          <div className="space-y-3" data-testid="sa-cp-signalements-list">
            {items.map((d) => (
              <div key={d.id} className="border border-red-200 rounded p-3 bg-red-50" data-testid={`sa-cp-signalement-${d.id}`}>
                <div className="flex justify-between">
                  <div>
                    <div className="font-medium">{d.type_demande} · {d.prenom} {d.nom}</div>
                    <div className="text-xs text-gray-600">{d.email} · {d.territoire}</div>
                    <div className="text-xs mt-1">
                      <Badge variant="destructive">{d.flag_category}</Badge>
                      <span className="ml-2 text-gray-700">{d.flag_reason}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Signalé le {formatDate(d.flagged_at)} par {d.flagged_by}
                    </div>
                  </div>
                  <div>
                    <Button size="sm" variant="outline"
                      onClick={() => setFlagModal({ open: true, demande: d })}
                      data-testid={`sa-cp-unflag-btn-${d.id}`}>
                      <FlagOff className="h-4 w-4 mr-1" /> Lever
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <FlagDemandeModal modal={flagModal} setModal={setFlagModal} onDone={fetchSignalements} />
      </CardContent>
    </Card>
  );
}
